import { SessionAdapter } from 'adapters/session.adapter';
import { BackendGateway } from 'gateway/backend.gateway';
import type { ISession } from 'types/session.type';

export class SessionsRepository {
  private static _instance: SessionsRepository | undefined;
  readonly CACHE_DURATION_MS = 5000; // 5 seconds

  private constructor(readonly backend: BackendGateway) {}

  static getInstance(backend: BackendGateway = BackendGateway.getInstance()): SessionsRepository {
    this._instance ??= new this(backend);
    return this._instance;
  }

  async init(login: string): Promise<ISession | undefined> {
    this.unsetCache();

    const response = await this.backend.call<{ session: ISession }>({
      uri: 'sessionInit',
      method: 'POST',
      data: { login },
    });
    if (response.ok) {
      const session = SessionAdapter.dtoToModel(response.result.session);
      return this.setCache(session);
    }

    console.error('Session could not be initialized', response.error);
    return undefined;
  }

  async login(code: string): Promise<ISession | undefined> {
    const initialSession = await this.get();
    if (!initialSession) {
      console.error('No session found');
      return undefined;
    }

    try {
      const response = await this.backend.call<{ session: ISession }>({
        uri: 'sessionLogin',
        method: 'POST',
        data: { sessionId: initialSession.id, code },
      });

      const sessionDto = response.result ? (response.result as { session: ISession }).session : undefined;
      const session = sessionDto ? SessionAdapter.dtoToModel(sessionDto) : undefined;
      return session ? this.setCache(session) : undefined;
    } catch (error) {
      console.error('Error logging in', error);
      return undefined;
    }
  }

  async get(): Promise<ISession | undefined> {
    const expiredSession = this.getCache(true);
    if (!expiredSession) return undefined;

    const session = this.getCache();
    if (session) return session;

    try {
      const sessionDto = await this.backend.callOrThrow<{ session: ISession }>({
        uri: `sessionDetails`,
        params: { sessionId: expiredSession.id },
      });
      const session = SessionAdapter.dtoToModel(sessionDto.session);
      return this.setCache(session);
    } catch (error) {
      console.error('Error fetching session', error);
      this.unsetCache();
      return undefined;
    }
  }

  async logout(): Promise<void> {
    const session = this.getCache();
    if (!session) return;
    const { id: sessionId } = session;
    this.unsetCache();

    try {
      await this.backend.callOrThrow({ uri: 'sessionLogout', method: 'POST', data: { sessionId } });
    } catch (error) {
      console.error('Error ending session', error);
    }
  }

  private setCache(session: ISession): ISession {
    localStorage.setItem('session', JSON.stringify(session));
    localStorage.setItem('sessionLastFetchCache', JSON.stringify(Date.now()));
    return session;
  }

  private unsetCache(): void {
    localStorage.removeItem('session');
    localStorage.removeItem('sessionLastFetchCache');
  }

  private getCache(evenIfExpired?: boolean): ISession | undefined {
    if (!evenIfExpired) {
      const lastFetchCache = localStorage.getItem('sessionLastFetchCache');
      if (!lastFetchCache) return undefined;
      const lastFetchCacheTime = JSON.parse(lastFetchCache) as number;
      if (Date.now() - lastFetchCacheTime > this.CACHE_DURATION_MS) {
        this.unsetCache();
        return undefined;
      }
    }

    const sessionJson = localStorage.getItem('session');
    if (!sessionJson) {
      this.unsetCache();
      return undefined;
    }

    return JSON.parse(sessionJson) as ISession;
  }
}
